import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { DoorOpen } from 'lucide-react';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  apiMessage,
  ALL_CREATED_RANGE,
  CREATED_RANGE_OPTIONS,
  createdRangeQuery,
  createdRangeValue,
  createdWindow,
  formatLocalDate,
  getWorkOnItem,
  parseLocalDateParam,
  tenantOptionLabel,
} from '../services/jobAuditApi';

const FILTER_CLASS =
  'mt-1 block w-40 rounded-md border border-gray-300 bg-white px-2 py-1.5 shadow-sm';

const EMPTY = {
  item_id: '',
  label: '',
  section: '',
  issue_label: '',
  failed: 0,
  applicable: 0,
  fail_pct: null,
  fields: [],
  tenants: [],
};

const clampPct = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.min(100, Math.max(0, n));
};

const sectionLabel = (section) => (section || '').replaceAll('_', ' ');

const DrillCue = () => (
  <span className="inline-flex shrink-0 text-blue-700" title="Open technicians">
    <DoorOpen className="h-4 w-4" aria-hidden="true" />
    <span className="sr-only">Open technicians</span>
  </span>
);

const FailBar = ({ failPct, applicable }) => {
  const pct = applicable ? clampPct(failPct) : 0;
  return (
    <div className="relative h-3 overflow-hidden rounded bg-gray-100" aria-hidden="true">
      {applicable ? (
        <>
          <div className="absolute inset-0 bg-gradient-to-r from-green-500 via-yellow-400 to-red-500" />
          <div className="absolute inset-y-0 right-0 bg-gray-100" style={{ width: `${100 - pct}%` }} />
        </>
      ) : null}
    </div>
  );
};

const JobAuditWorkOn = () => {
  const { itemId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const tenant = searchParams.get('tenant') || '';
  const rangeParam = searchParams.get('range') || '';
  const on = searchParams.get('on') || '';
  const rangeSelect = createdRangeValue(rangeParam, on);
  const range = createdRangeQuery(rangeParam, on);
  const onDate = parseLocalDateParam(on);

  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState(false);
  const [data, setData] = useState(EMPTY);

  const setFilter = useCallback(
    (patch) => {
      const next = new URLSearchParams(searchParams);
      Object.entries(patch).forEach(([key, value]) => {
        if (value) next.set(key, value);
        else next.delete(key);
      });
      setSearchParams(next);
    },
    [searchParams, setSearchParams]
  );

  const filterQuery = () => {
    const params = new URLSearchParams();
    if (tenant) params.set('tenant', tenant);
    if (on) params.set('on', on);
    else if (range) params.set('range', range);
    else if (rangeSelect === ALL_CREATED_RANGE) params.set('range', ALL_CREATED_RANGE);
    return params;
  };

  const summaryHref = (() => {
    const qs = filterQuery().toString();
    return qs ? `/audit/summary?${qs}` : '/audit/summary';
  })();

  const techniciansHref = (fieldId = '') => {
    const params = filterQuery();
    if (fieldId) params.set('field', fieldId);
    const qs = params.toString();
    const path = `/audit/summary/${encodeURIComponent(itemId)}/technicians`;
    return qs ? `${path}?${qs}` : path;
  };

  useEffect(() => {
    let cancelled = false;
    const bounds = createdWindow({ range, on });
    setLoading(true);
    setMissing(false);
    getWorkOnItem(itemId, {
      tenant,
      created_after: bounds.created_after || '',
      created_before: bounds.created_before || '',
    })
      .then((payload) => {
        if (!cancelled) setData(payload);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          setMissing(true);
          setData(EMPTY);
          return;
        }
        toast.error(apiMessage(err, 'Failed to load this checklist item'));
        setData(EMPTY);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [itemId, tenant, range, on]);

  const section = sectionLabel(data.section);

  return (
    <div className="space-y-4">
      <div>
        <Link to={summaryHref} className="text-sm text-blue-700 hover:underline">
          ← Summary
        </Link>
        <h1 className="mt-2 text-2xl font-semibold text-gray-900">
          {data.issue_label || 'Checklist item'}
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          Fields inside this checklist item, and how often each one fails.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-lg bg-white p-4 shadow">
        <label className="text-sm text-gray-700">
          Tenant
          <select
            className="mt-1 block w-56 rounded-md border border-gray-300 bg-white px-2 py-1.5 shadow-sm"
            value={tenant}
            onChange={(e) => setFilter({ tenant: e.target.value })}
          >
            <option value="">all</option>
            {(data.tenants || []).map((option) => (
              <option key={option.code || option} value={option.code || option}>
                {tenantOptionLabel(option)}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm text-gray-700">
          Created
          <select
            className={FILTER_CLASS}
            value={rangeSelect}
            onChange={(e) => setFilter({ range: e.target.value, on: '' })}
          >
            <option value={ALL_CREATED_RANGE}>all</option>
            {CREATED_RANGE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm text-gray-700">
          Date
          <DatePicker
            selected={onDate}
            onChange={(date) => setFilter({ on: date ? formatLocalDate(date) : '', range: '' })}
            dateFormat="yyyy-MM-dd"
            isClearable
            placeholderText="pick a day"
            wrapperClassName="mt-1 block"
            className="block w-40 rounded-md border border-gray-300 bg-white px-2 py-1.5 shadow-sm"
          />
        </label>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-lg bg-white py-16 shadow">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
          <p className="text-sm text-gray-600">Loading checklist item…</p>
        </div>
      ) : missing ? (
        <div className="rounded-lg bg-white p-6 text-sm text-gray-600 shadow">
          This checklist item is not in the score policy.
        </div>
      ) : (
        <>
          <Link
            to={techniciansHref()}
            className="block rounded-lg bg-white p-4 shadow hover:ring-2 hover:ring-blue-300"
          >
            <div className="mb-1 flex items-center justify-between gap-3 text-sm">
              <span className="font-medium text-gray-900">{data.issue_label}</span>
              <span className="flex shrink-0 items-center gap-2">
                <span className="tabular-nums text-gray-700">
                  {data.fail_pct == null ? '—' : `${data.fail_pct}%`}
                </span>
                <DrillCue />
              </span>
            </div>
            <FailBar failPct={data.fail_pct} applicable={data.applicable} />
            <div className="mt-1 text-xs text-gray-500">
              {data.failed}/{data.applicable}
              {section ? <span className="capitalize">{` · ${section}`}</span> : null}
            </div>
          </Link>

          <div className="rounded-lg bg-white p-4 shadow">
            <h2 className="text-sm font-semibold text-gray-800">Fields</h2>
            <p className="mt-1 text-xs text-gray-500">
              Ranked by how often the field fails on audits where it applies.
            </p>
            <ul className="mt-4 space-y-4">
              {(data.fields || []).map((row) => (
                <li key={row.field_id}>
                  <Link
                    to={techniciansHref(row.field_id)}
                    className="block rounded-md px-2 py-2 -mx-2 hover:bg-gray-50"
                  >
                    <div className="mb-1 flex items-center justify-between gap-3 text-sm">
                      <span className="font-medium text-gray-900">{row.label}</span>
                      <span className="flex shrink-0 items-center gap-2">
                        <span className="tabular-nums text-gray-700">
                          {row.fail_pct == null ? '—' : `${row.fail_pct}%`}
                        </span>
                        <DrillCue />
                      </span>
                    </div>
                    <FailBar failPct={row.fail_pct} applicable={row.applicable} />
                    <div className="mt-1 text-xs text-gray-500">
                      {row.applicable
                        ? `${row.failed}/${row.applicable}`
                        : 'No applicable audits in this window'}
                    </div>
                  </Link>
                </li>
              ))}
              {(data.fields || []).length === 0 && (
                <li className="text-sm text-gray-500">
                  This checklist item has no fields to break out.
                </li>
              )}
            </ul>
          </div>
        </>
      )}
    </div>
  );
};

export default JobAuditWorkOn;
